import { Shield, Hammer, Sun, Droplets, ArrowRight, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import serviceRoofing from "@/assets/service-roofing.jpg";
import serviceConstruction from "@/assets/service-construction.jpg";
import serviceSolar from "@/assets/service-solar.jpg";
import serviceMold from "@/assets/service-mold.jpg";
import BackgroundPattern from "@/components/BackgroundPattern";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Shield,
    title: "Roofing",
    headline: "Durable Roofs Built for NM Weather",
    description:
      "Asphalt shingles, metal, tile, and slate. Expert installations, repairs, and full replacements backed by Owens Corning warranties.",
    benefits: ["All major roofing materials", "Storm damage & insurance claims", "Lifetime warranty options"],
    href: "/services/roofing",
    image: serviceRoofing,
    alt: "Professional roof installation on Albuquerque home by Zenith Solutions",
  },
  {
    icon: Hammer,
    title: "General Construction",
    headline: "From Remodels to Additions—Done Right",
    description:
      "Home additions, kitchens, bathrooms, windows, painting, and stucco. Any size project, transparent pricing, zero shortcuts.",
    benefits: ["Home additions & remodels", "Windows, doors & painting", "Transparent line-item pricing"],
    href: "/services/construction",
    image: serviceConstruction,
    alt: "Home remodeling and construction project in New Mexico",
  },
  {
    icon: Sun,
    title: "Solar",
    headline: "Slash Bills with Custom Solar Power",
    description:
      "We explain every NM tax credit and incentive, use tier-1 technology, and design systems for maximum sustainable savings.",
    benefits: ["40-70% bill reduction", "NM tax credits & incentives", "Zero-down financing available"],
    href: "/services/solar",
    image: serviceSolar,
    alt: "Solar panel installation on residential home in Albuquerque New Mexico",
  },
  {
    icon: Droplets,
    title: "Mold & Water Mitigation",
    headline: "Fast Response. Complete Remediation.",
    description:
      "Professional mold removal and water damage restoration. We identify the source, contain damage, and restore your property safely.",
    benefits: ["24/7 emergency response", "Certified remediation", "Insurance claim support"],
    href: "/services/mold-remediation",
    image: serviceMold,
    alt: "Mold remediation and water damage restoration service in New Mexico",
  },
];

const ServicesSection = () => (
  <section
    id="services"
    className="section-padding bg-section-mid relative"
    aria-label="Roofing, solar, construction and mold remediation services in Albuquerque NM"
  >
    <BackgroundPattern variant="blueprint" />
    <div className="container mx-auto max-w-6xl relative z-10">
      <div className="text-center mb-16">
        <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Our Services</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground">
          Albuquerque's Full-Service Contractor
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          Roofing, solar, construction, and mold remediation — all from one licensed and insured New Mexico contractor
          trusted by 1,000+ homeowners.
        </p>
      </div>

      {/* Alternating full-width service cards */}
      <div className="space-y-8">
        {services.map((s, i) => (
          <motion.article
            key={s.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="bg-card rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow group"
          >
            <div className="grid md:grid-cols-2">
              {/* Image */}
              <div className={`h-64 md:h-auto min-h-[280px] overflow-hidden ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <img
                  src={s.image}
                  alt={s.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>

              {/* Content */}
              <div className={`p-8 md:p-10 flex flex-col justify-center ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <s.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-primary">{s.title}</p>
                </div>

                <h3 className="text-2xl md:text-3xl font-black text-foreground mb-3">{s.headline}</h3>
                <p className="text-muted-foreground leading-relaxed mb-5">{s.description}</p>

                <ul className="space-y-2 mb-6">
                  {s.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-foreground">
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" aria-hidden="true" /> {b}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={s.href}>
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold gap-2 shadow-md">
                      {s.title} Services <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <a href="tel:5056048536">
                    <Button
                      variant="outline"
                      className="font-bold gap-2 border-primary/30 text-foreground hover:bg-primary/5 hover:text-foreground"
                    >
                      <Phone className="h-4 w-4" /> Call for Quote
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

export default ServicesSection;
